import React, { useEffect, useMemo, useState } from 'react';
import { useGqlMutation } from '@API/useAvQuery';
import CodeEditor from '@components/CodeEditor';
import AvTable from '@components/Table/AvTable';
import { AttentionBoxVariant, AvAttentionBox, AvTooltip, ColumnReorder, TextInput } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Button, Divider, Skeleton, Typography, useTheme } from '@mui/material';
import { getIfValidStringifiedObject, getNetworkErrorCode } from '@utils/utils';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useParams } from '../../../utils/AvRouter';
import { setNestedValue } from '../../../utils/set-nested-value';
import { useMappingContext } from './MappingContext';
import { PREVIEW_PAGE_SIZE } from './types';
import { extractPropertiesFromScript, getHeadCells } from './utils';

const EXECUTE_SCRIPT_QUERY = gql`
  mutation executePythonScript($script: String!, $jsonData: String!) {
    executePythonScript(script: $script, jsonData: $jsonData)
  }
`;

const ScriptEditorTest = ({
  value: mappingItemEditorValue,
  onChange: onChangeMappingItem,
}: {
  value?: string;
  onChange?: (value: string) => void;
}) => {
  const theme = useTheme();
  const { previewRows, previewPaginationProps, parsedFields, workflowId, isLoadingPreviewMappingQuery } = useMappingContext();
  const { id: instanceId } = useParams();

  const [fieldInScriptUserValue, setFieldInScriptUserValue] = useState({});
  const [testResult, setTestResult] = useState<any>(undefined);
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: runTest, isPending: isTesting } = useGqlMutation({
    mutation: EXECUTE_SCRIPT_QUERY,
    onSuccess: result => {
      setTestResult(JSON.stringify(result));
    },
    onError: error => {
      if (getNetworkErrorCode(error) === 400) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        throw error;
      }
    },
  });

  const fieldsInScript = useMemo(
    () => extractPropertiesFromScript(typeof mappingItemEditorValue === 'string' ? mappingItemEditorValue : ''),
    [mappingItemEditorValue]
  );

  const initialHeadCells = useMemo(() => {
    const allCells = getHeadCells({ headers: parsedFields, instanceId, lastWfRun: workflowId });
    return allCells.length > 30 ? allCells.map((hc, index) => ({ ...hc, hidden: index > 30 })) : allCells;
  }, [parsedFields, instanceId, workflowId]);

  const [tableHeadCells, setTableHeadCells] = useState(initialHeadCells);

  useEffect(() => {
    setTableHeadCells(initialHeadCells);
  }, [initialHeadCells]);

  const setFieldDataFromTableRow = (rowIndex: number) => {
    if (previewRows?.[rowIndex]) {
      const selectedRow = previewRows[rowIndex];

      setFieldInScriptUserValue(
        fieldsInScript.reduce((acc, field) => {
          if (selectedRow[field] !== undefined) {
            return { ...acc, [field]: selectedRow[field] };
          }

          const nestedKeys = Object.keys(selectedRow).filter(key => key.startsWith(`${field}.`));

          if (nestedKeys.length > 0) {
            const subObject = nestedKeys.reduce((currentSubObject, nestedKey) => {
              const path = nestedKey.substring(field.length + 1);
              return setNestedValue(currentSubObject, path, selectedRow[nestedKey]);
            }, {} as any);

            return { ...acc, [field]: subObject };
          }
          return { ...acc, [field]: selectedRow[field] };
        }, {})
      );
    }
  };

  const handleRunTest = () => {
    setTestResult(undefined);
    const jsonData = JSON.stringify(fieldInScriptUserValue);
    runTest({ script: mappingItemEditorValue, jsonData });
  };
  return (
    <Box sx={{ ...flex.row, width: '100%', height: '88%', flexBasis: '40%', gap: 2 }}>
      <CodeEditor value={mappingItemEditorValue as string} onChange={onChangeMappingItem} height="100%" />
      <Box
        sx={{
          flexBasis: '60%',
          ...flex.col,
          width: '60%',
          borderRadius: 2,
          border: '1px solid',
          borderColor: theme.palette.colors.neutrals[400],
          flexShrink: 0,
        }}>
        <Box sx={{ minHeight: 200, maxHeight: 300, px: 3, pt: 2, overflow: 'auto' }}>
          <Typography variant="h6">Test Logic</Typography>
          <div>Manually populate input fields or select a record from the table</div>
          <Box sx={{ ...flex.row, pt: 1, flexWrap: 'wrap', gap: 2 }}>
            {fieldsInScript.length ? (
              fieldsInScript.map(f => (
                <TextInput
                  key={f}
                  label={f || ' '}
                  size="small"
                  sx={{ width: 200 }}
                  onChange={v => {
                    setFieldInScriptUserValue(prev => ({ ...prev, [f]: getIfValidStringifiedObject(v, true) || v }));
                  }}
                  value={
                    typeof fieldInScriptUserValue[f] === 'object' ? JSON.stringify(fieldInScriptUserValue[f]) : fieldInScriptUserValue[f]
                  }
                />
              ))
            ) : (
              <AvAttentionBox
                sx={{ width: '100%', textAlign: 'center', pt: 1.5 }}
                showIcon={false}
                variant={AttentionBoxVariant.Neutral}
                content="Edit script to expose input fields for logic test"
              />
            )}
          </Box>
        </Box>
        <Box sx={{ ...flex.col, maxHeight: '100%', overflow: 'auto' }}>
          <Box sx={{ ...flex.row, alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Typography variant="h6" sx={{ py: 2, pl: 3 }}>
              Select a record to populate input fields and test logic
            </Typography>
            <ColumnReorder items={tableHeadCells} setItems={setTableHeadCells} initialColumns={initialHeadCells} />
          </Box>
          <AvTable
            allowEdit={false}
            pagination
            resizable
            size="xSmall"
            headCells={tableHeadCells}
            enableSelectionColor={false}
            rows={previewRows}
            paginationProps={previewPaginationProps}
            loading={isLoadingPreviewMappingQuery}
            rowLimit={PREVIEW_PAGE_SIZE}
            onActive={fieldsInScript.length ? setFieldDataFromTableRow : undefined}
            sx={{ pl: 3 }}
          />
          <Divider />
          <Box sx={{ ...flex.row, alignItems: 'center', p: 2, gap: 2, flex: 1, minHeight: 70 }}>
            {testResult !== undefined && (
              <Box sx={{ ...ellipsis, borderRadius: 1, background: theme => theme.palette.colors.green[100], py: 1, px: 2 }}>
                <AvTooltip>{testResult}</AvTooltip>
              </Box>
            )}
            {isTesting && <Skeleton sx={{ height: 37, width: 150 }} />}
            <span style={{ flex: 1 }} />
            <Button disabled={isTesting} sx={{ flexShrink: 0, justifySelf: 'end' }} variant="outlined" size="small" onClick={handleRunTest}>
              Run Test
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ScriptEditorTest;
